import React, { useState, useEffect } from 'react';
import { Grid, Typography, FormHelperText, FormControl, Select, MenuItem, Button, Card, CardContent, List, ListItem, ListItemText, Divider, IconButton, Collapse, Chip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FieldGroup from '@core-ui-shared/fieldGroup';
import EditPageUpperContainer from '../../../components/Shared/EditPageHeader';
import EditPageLowerContainer from '../../../components/Shared/EditPageLowerContainer';
import { ShippingCarrierAPI } from '../../../api/shippingCarrierApi';
import ShippingZoneModal from './ShippingZoneModal';
import ShippingLocationModal from './ShippingLocationModal';
import RateModal from './RateModal';
import ShippingCarrierModal from './ShippingCarrierModal';
import { useShippingProfile } from '../contexts/ShippingProfileContext';
import useTranslation from '../../../hooks/useTranslation';
import ShippingRatesPreview from './ShippingRatesPreview';

const EditShippingProfile = ({ isEditMode, onSave }) => {
  const { t } = useTranslation();
  const { shippingProfile, setShippingProfile, shipmentLocation, setShipmentLocation, selectedZones, setSelectedZones, selectedCarriers, setSelectedCarriers, validationErrors, fulfillmentCenters, onSelectZone, suggestedCarriers, setSuggestedCarriers } = useShippingProfile();

  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [isCarrierModalOpen, setIsCarrierModalOpen] = useState(false);
  const [currentZoneIndex, setCurrentZoneIndex] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [rateBeingEdited, setRateBeingEdited] = useState(null);
  const [zoneBeingEdited, setZoneBeingEdited] = useState(null);
  const [expandedZones, setExpandedZones] = useState({});

  const toggleZoneExpansion = (zoneIndex) => {
    setExpandedZones((prev) => ({
      ...prev,
      [zoneIndex]: !prev[zoneIndex],
    }));
  };

  const handleOpenRateModal = (zoneIndex) => {
    setCurrentZoneIndex(zoneIndex);
    setIsRateModalOpen(true);
  };

  const handleEditZone = (zoneIndex, zone) => {
    setCurrentZoneIndex(zoneIndex);
    setZoneBeingEdited(zone);
    setIsZoneModalOpen(true);
  };

  const handleSaveZone = (updatedZone) => {
    const updatedZones = [...selectedZones];
    if (zoneBeingEdited) {
      updatedZones[currentZoneIndex] = updatedZone;
    } else {
      updatedZones.push(updatedZone);
    }
    setSelectedZones(updatedZones);
    setIsZoneModalOpen(false);
    setZoneBeingEdited(null);
  };

  const handleSaveRate = (rate) => {
    const updatedZones = [...selectedZones];
    if (rateBeingEdited) {
      updatedZones[currentZoneIndex].shippingRates[rateBeingEdited.index] = rate;
    } else {
      updatedZones[currentZoneIndex].shippingRates = [
        ...(updatedZones[currentZoneIndex].shippingRates || []),
        rate,
      ];
    }
    setSelectedZones(updatedZones);
    setIsRateModalOpen(false);
    setRateBeingEdited(null);
  };
  
  const handleSaveLocation = (locationData) => {
    setShipmentLocation(locationData);
    setIsLocationModalOpen(false);
  };

  const handleDeleteZone = (zoneIndex) => {
    setSelectedZones(selectedZones.filter((_, index) => index !== zoneIndex));
  };

  const handleDeleteCarrier = (carrierIndex) => {
    setSelectedCarriers(selectedCarriers.filter((_, index) => index !== carrierIndex));
  };

  const fetchSuggestedCarriers = async () => {
    if (shipmentLocation?.fulfillment_center_id) {
      try {
        const response = await ShippingCarrierAPI.getShippingCarriers(
          shipmentLocation.fulfillment_center_id
        );
        setSuggestedCarriers(response.data);
      } catch (error) {
        console.error('Error fetching suggested shipping carriers:', error);
      }
    }
  };

  const handleAddCarrier = (carrier) => {
    setSelectedCarriers((prev) => [...prev, carrier]);
  };

  const handleRemoveCarrier = (carrierId) => {
    setSelectedCarriers((prev) =>
      prev.filter((carrier) => carrier.id !== carrierId)
    );
  };

  useEffect(() => {
    fetchSuggestedCarriers();
  }, [shipmentLocation]);

  const handleEditRate = (zoneIndex, rateIndex, rate) => {
    setCurrentZoneIndex(zoneIndex);
    setRateBeingEdited({ index: rateIndex, data: rate });
    setIsRateModalOpen(true);
  };

  const handleDeleteRate = (zoneIndex, rateIndex) => {
    const updatedZones = [...selectedZones];
    updatedZones[zoneIndex].shippingRates.splice(rateIndex, 1);
    setSelectedZones(updatedZones);
  };  

  const leftContent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FieldGroup
          id="shippingProfileName"
          type="text"
          label={t('labels.shipping_profile.name')}
          name="name"
          value={shippingProfile.name || ''}
          validationState={validationErrors?.name ? 'error' : null}
          placeholder={t('labels.shipping_profile.enter_name')}
          onChange={(e) =>
            setShippingProfile({ ...shippingProfile, [e.target.name]: e.target.value })
          }
          required
        />
        {validationErrors?.name && (
          <FormHelperText error>{validationErrors.name}</FormHelperText>
        )}
      </Grid>
      {isEditMode && (
        <Grid item xs={12} style={{ marginTop: '20px' }}>
        <Grid item xs={12} md={12}>
          <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('labels.shipping_profile.location')}
            <Button variant="contained" color="primary" onClick={() => setIsLocationModalOpen(true)}>
              {t('labels.shipping_profile.manage_location')}
            </Button>
          </Typography>

          <Card variant="outlined" style={{ marginTop: '20px' }}>
            <CardContent>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                {shipmentLocation.name || t('labels.shipping_profile.no_location')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {shipmentLocation.address || t('labels.shipping_profile.no_address')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Card variant="outlined" style={{ marginTop: '20px' }}>
          <CardContent>
          <Grid item xs={12} style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Typography variant="h6">{t('labels.shipping_profile.shipping_zones')}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsZoneModalOpen(true)}
          >
            {t('labels.shipping_profile.add_shipping_zone')}
          </Button>
        </div>
        {selectedZones.length > 0 ? (
          selectedZones.map((zone, index) => (
            <Card key={index} variant="outlined" style={{ marginBottom: '20px' }}>
              <CardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    {zone.name}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <IconButton onClick={() => handleDeleteZone(index)} style={{ color: 'black' }}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEditZone(index, zone)} style={{ color: 'black' }}>
                      <EditIcon />
                    </IconButton>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('labels.shipping_profile.regions')}:
                    {' '}
                    {(zone.regions || []).length > 0 && [...new Set(zone.regions.map(r => r.region_code))].map(regionCode => {
                      const countriesInRegion = zone.regions.filter(r => r.region_code === regionCode);
                      return (
                        <Chip
                          key={regionCode}
                          label={`${regionCode} (${countriesInRegion.length})`}
                          size="small"
                          style={{ marginRight: 6 }}
                        />
                      );
                    })}
                  </Typography>
                  <IconButton size="small" onClick={() => toggleZoneExpansion(index)}>
                    {expandedZones[index] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </div>
                <Collapse in={expandedZones[index]}>
                  <Typography variant="body2" color="textSecondary">
                    {(zone.regions || []).map(region => `${region.flag} ${region.name}`).join(', ')}
                  </Typography>
                </Collapse>
                {zone.shippingRates?.length > 0 ? (
                  <div style={{ marginTop: '20px' }}>
                    <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                      {t('labels.shipping_profile.rates')}
                    </Typography>
                    <Divider style={{ marginBottom: '10px' }} />
                    {zone.shippingRates.map((rate, rateIndex) => (
                      <div key={rateIndex} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {rate.name || 'Shipping'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {rate.description || 'Flat rate'}
                            { (rate.min_price != null || rate.max_price != null) && (
                              <>
                                {' — Order value: '}
                                {rate.min_price != null ? Number(rate.min_price) : 0}
                                {' to '}
                                {rate.max_price != null ? Number(rate.max_price) : 'No limit'}
                              </>
                            )}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Typography variant="body1" sx={{ color: '#333' }}>
                            {Number(rate.price).toFixed(2)}
                          </Typography>
                          <IconButton onClick={() => handleEditRate(index, rateIndex, rate)} style={{ color: 'black' }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteRate(index, rateIndex)} style={{ color: 'black' }}>
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Typography color="textSecondary" style={{ marginTop: '10px' }}>
                    {t('labels.shipping_profile.no_rates')}
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => handleOpenRateModal(index)}
                  style={{ marginTop: '20px' }}
                >
                  {t('labels.shipping_profile.add_rate')}
                </Button>
              </CardContent>
            </Card>
            ))
          ) : (
            <Typography color="textSecondary">
              {t('labels.shipping_profile.no_shipping_zones')}
            </Typography>
          )}
        </Grid>
            </CardContent>
          </Card>
          <Button
            variant="outlined"
            onClick={() => setIsZoneModalOpen(true)}
            style={{ marginTop: '10px' }}
          >
            {t('labels.shipping_profile.add_shipping_zone')}
          </Button>
        </Grid>
      )}
    </Grid>
  );

  const rightContent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography gutterBottom variant="h5" component="h2">
          {t('labels.shipping_profile.status')}
        </Typography>
        <FormControl fullWidth>
          <Select
            name="status"
            value={shippingProfile.status || 'inactive'}
            onChange={(e) =>
              setShippingProfile({ ...shippingProfile, [e.target.name]: e.target.value })
            }
          >
            <MenuItem value="active">{t('labels.shipping_profile.active')}</MenuItem>
            <MenuItem value="inactive">{t('labels.shipping_profile.inactive')}</MenuItem>
          </Select>
          {validationErrors?.status && (
            <FormHelperText error>{validationErrors.status}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      {isEditMode && (
        <Grid item xs={12} style={{ marginTop: '20px' }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">{t('labels.shipping_profile.shipping_carriers')}</Typography>
              <Divider style={{ marginBottom: '10px' }} />
              <List>
                {selectedCarriers?.length > 0 ? (
                  selectedCarriers.map((carrier, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={carrier.name}
                        secondary={
                          carrier.tracking_url
                            ? `${t('labels.shipping_profile.tracking_url')}: ${carrier.tracking_url}`
                            : t('labels.shipping_profile.no_tracking_url')
                        }
                      />
                      <IconButton
                        onClick={() => handleDeleteCarrier(index)}
                        style={{ color: 'black' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))
                ) : (
                  <Typography color="textSecondary">
                    {t('labels.shipping_profile.no_carriers')}
                  </Typography>
                )}
              </List>
              <Button
                variant="outlined"
                onClick={() => setIsCarrierModalOpen(true)}
                style={{ marginTop: '10px' }}
              >
                {t('labels.shipping_profile.add_carrier')}
              </Button>
            </CardContent>
          </Card>
          {shipmentLocation?.fulfillment_center_id && (
        <Card variant="outlined" style={{ marginTop: '20px' }}>
          <CardContent>
            <Typography variant="h6">{t('labels.shipping_profile.suggested_carriers')}</Typography>
            <Divider style={{ marginBottom: '10px' }} />
            <List>
              {suggestedCarriers?.length > 0 ? (
                suggestedCarriers.map((carrier) => (
                  <ListItem key={carrier.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ListItemText
                      primary={carrier.name}
                      secondary={carrier.tracking_url || t('labels.shipping_profile.no_tracking_url')}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => handleAddCarrier(carrier)}
                      disabled={selectedCarriers.some((selected) => selected.id === carrier.id)}
                    >
                      {t('labels.shipping_profile.add')}
                    </Button>
                  </ListItem>
                ))
              ) : (
                <Typography color="textSecondary">
                  {t('labels.shipping_profile.no_suggested_carriers')}
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>)}
          <ShippingRatesPreview zones={selectedZones} />
        </Grid>
      )}
    </Grid>
  );

  return (
    <div>
      <EditPageUpperContainer
        actionType={isEditMode ? t('labels.shipping_profile.edit') : t('labels.shipping_profile.new')}
        page={t('labels.shipping_profile.page')}
        onSave={onSave}
      />
      <EditPageLowerContainer
        leftContent={[leftContent]}
        rightContent={[rightContent]}
        actionType={isEditMode ? t('labels.shipping_profile.edit') : t('labels.shipping_profile.new')}
        onSave={onSave}
      />
      <ShippingZoneModal
        open={isZoneModalOpen}
        onClose={() => setIsZoneModalOpen(false)}
        onSelectZone={onSelectZone}
        onSaveZone={handleSaveZone}
        existingZone={zoneBeingEdited}
      />
      <RateModal
        open={isRateModalOpen}
        onClose={() => setIsRateModalOpen(false)}
        onSaveRate={handleSaveRate}
        existingRate={rateBeingEdited?.data}
      />
      <ShippingCarrierModal
        open={isCarrierModalOpen}
        onClose={() => setIsCarrierModalOpen(false)}
      />
      <ShippingLocationModal
        open={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onSave={handleSaveLocation}
        fulfillmentCenters={fulfillmentCenters}
        initialData={shipmentLocation}
      />
    </div>
  );
};

export default EditShippingProfile;